import { create } from 'zustand';

/**
 * Two disjoint vertex selections for ML Reduction (indices into shadedMesh.geometry.position).
 */
const useMLReductionStore = create((set) => ({
  regionA: /** @type {Set<number>} */ (new Set()),
  regionB: /** @type {Set<number>} */ (new Set()),
  activeRegion: /** @type {'A' | 'B'} */ ('A'),

  /** Shift+LMB brush (same interaction pattern as blocking zone). */
  brush: {
    radius: 1.0,
    mode: /** @type {'paint' | 'erase'} */ ('paint'),
  },

  /** Incremented when UI requests mesh reduction; MLReductionHandler applies deformation */
  applyReductionRequestId: 0,

  /** Incremented so MLReductionHandler restores vertices from reductionPositionBackup */
  undoReductionRequestId: 0,

  /**
   * Copy of position BufferAttribute array immediately before the last reduction apply.
   * @type {Float32Array | Uint32Array | null}
   */
  reductionPositionBackup: null,
  /** True when reductionPositionBackup exists for Undo */
  canUndoReduction: false,

  /** After a successful Apply, blocks applying again until Undo or reset */
  mlReductionApplyConsumed: false,

  /** Geodesic distance between regions in mm, or null if not computed / unavailable */
  distanceMm: /** @type {number | null} */ (null),
  /** True when a valid numeric distance was computed */
  isDistanceReady: false,
  /** User-requested reduction (mm), clamped to [0, distanceMm] */
  reductionMm: 0,

  setRegionA: (regionA) => set({ regionA }),
  setRegionB: (regionB) => set({ regionB }),
  setActiveRegion: (activeRegion) => set({ activeRegion }),

  setMlReductionBrush: (patch) =>
    set((state) => ({
      brush: { ...state.brush, ...patch },
    })),

  requestApplyReduction: () =>
    set((s) => ({ applyReductionRequestId: s.applyReductionRequestId + 1 })),

  requestUndoReduction: () =>
    set((s) => ({ undoReductionRequestId: s.undoReductionRequestId + 1 })),

  /** Called by MLReductionHandler before mutating geometry */
  setReductionPositionBackup: (typedArray) =>
    set({
      reductionPositionBackup: typedArray,
      canUndoReduction: true,
    }),

  clearReductionUndoSnapshot: () =>
    set({
      reductionPositionBackup: null,
      canUndoReduction: false,
    }),

  setMlReductionApplyConsumed: (consumed) => set({ mlReductionApplyConsumed: consumed }),

  clearRegionA: () => set({ regionA: new Set() }),
  clearRegionB: () => set({ regionB: new Set() }),

  /** After successful reduction: empty regions, distance, and reduction. */
  clearSelectionAfterReduction: () =>
    set({
      regionA: new Set(),
      regionB: new Set(),
      distanceMm: null,
      isDistanceReady: false,
      reductionMm: 0,
    }),
  clearAll: () =>
    set({
      regionA: new Set(),
      regionB: new Set(),
      activeRegion: 'A',
      brush: { radius: 1.0, mode: 'paint' },
      applyReductionRequestId: 0,
      undoReductionRequestId: 0,
      reductionPositionBackup: null,
      canUndoReduction: false,
      mlReductionApplyConsumed: false,
      distanceMm: null,
      isDistanceReady: false,
      reductionMm: 0,
    }),

  resetMLReduction: () =>
    set({
      regionA: new Set(),
      regionB: new Set(),
      activeRegion: 'A',
      brush: { radius: 1.0, mode: 'paint' },
      applyReductionRequestId: 0,
      undoReductionRequestId: 0,
      reductionPositionBackup: null,
      canUndoReduction: false,
      mlReductionApplyConsumed: false,
      distanceMm: null,
      isDistanceReady: false,
      reductionMm: 0,
    }),

  /**
   * @param {number | null} mm - null clears distance and resets reduction to 0
   */
  setDistanceMm: (mm) =>
    set((state) => {
      if (mm === null || !Number.isFinite(mm)) {
        return {
          distanceMm: null,
          isDistanceReady: false,
          reductionMm: 0,
        };
      }
      const clampedRed = Math.min(state.reductionMm, mm);
      return {
        distanceMm: mm,
        isDistanceReady: true,
        reductionMm: Math.max(0, clampedRed),
      };
    }),

  /** Clamp reduction into [0, distanceMm]; if no distance, force 0 */
  setReductionMm: (value) =>
    set((state) => {
      const max = state.distanceMm ?? 0;
      const v = Number(value);
      const n = Number.isFinite(v) ? v : 0;
      return { reductionMm: Math.max(0, Math.min(n, max)) };
    }),

  recomputeClamp: () =>
    set((state) => {
      const max = state.distanceMm ?? 0;
      return {
        reductionMm: Math.max(0, Math.min(state.reductionMm, max)),
      };
    }),
}));

export default useMLReductionStore;
